import { profileAxiosInstance as network } from '../network/axios';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Endpoints, SiteTypes } from '../network/enums';
import { endpointReplace } from '../network/utils/endpointReplace';
import { MenuData } from '../components/Modals/LoginModal/menu-types';
import { RequestTimeout } from '../network/types';
import { isCancel } from 'axios';

type FetchResult =
  | {
      status: 'Menu';
      data: MenuData;
    }
  | {
      status: 'MenuLoading';
    }
  | {
      status: 'MenuError';
      error: Error;
    };

export const useMenu = (enabled: boolean, onAuthEvent: (result: FetchResult) => void) => {
  const [fetchState, setFetchState] = useState<FetchResult>();
  const [retryCount, setRetryCount] = useState(0);

  // we don't want to rerun useEffect if onAuthEvent changes
  const onAuthEventExtRef = useRef(onAuthEvent);
  onAuthEventExtRef.current = onAuthEvent;

  const emit = useCallback((result: FetchResult) => {
    onAuthEventExtRef.current(result);
    setFetchState(result);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const controller = new AbortController();
    emit({ status: 'MenuLoading' });

    network
      .get(endpointReplace({ ':siteType': SiteTypes.PROFILE }, Endpoints.Menu), {
        signal: controller.signal,
        timeout: RequestTimeout.L,
      })
      .then((menuData) => {
        if (!controller.signal.aborted) {
          if (menuData) {
            emit({ status: 'Menu', data: menuData.data });
          } else {
            emit({ status: 'MenuError', error: new Error('Could not fetch menu') });
          }
        }
      })
      .catch((error) => {
        if (isCancel(error)) {
          return;
        }
        console.error(error);
        emit({ status: 'MenuError', error });
      });

    return () => {
      controller.abort();
    };
  }, [enabled, retryCount, emit]);

  return useMemo(
    () => ({
      cached: fetchState?.status === 'Menu' ? fetchState.data : undefined,
      clearCache: () => setFetchState(undefined),
      retry: () => setRetryCount((r) => r + 1),
    }),
    [fetchState],
  );
};
